import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Coffee } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirect = location.state?.from || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const r = await login(email, password);
    setLoading(false);
    if (r.ok) navigate(redirect, { replace: true });
    else setError(r.error);
  };

  return (
    <div className="min-h-[calc(100vh-64px)] grid md:grid-cols-2" data-testid="login-page">
      <div className="hidden md:block relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1442512595307-53112b14a650?q=80&w=1400&auto=format&fit=crop"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#2C1E16]/70 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-[#F9F6F0]">
          <Coffee className="h-7 w-7 mb-4" strokeWidth={1.5} />
          <p className="font-serif text-3xl leading-tight max-w-md">
            "The morning ritual is sacred. The cafe is its temple."
          </p>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-16">
        <form onSubmit={handle} className="w-full max-w-sm">
          <p className="text-xs uppercase tracking-[0.25em] text-[#786B63]">Welcome back</p>
          <h1 className="font-serif text-4xl mt-3">Sign in to BrewFinder</h1>
          <p className="text-sm text-[#786B63] mt-2">
            Save favorites, write reviews, and submit your discoveries.
          </p>

          <div className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1.5 rounded-sm bg-[#F9F6F0] border-border h-11"
                data-testid="login-email"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1.5 rounded-sm bg-[#F9F6F0] border-border h-11"
                data-testid="login-password"
              />
            </div>
          </div>

          {error && (
            <p className="text-sm text-[#C05A46] mt-4" data-testid="login-error">{error}</p>
          )}

          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="mt-6 w-full rounded-sm bg-[#2C1E16] hover:bg-[#1d130d] text-[#F9F6F0] h-11"
          >
            {loading ? "Signing in…" : "Sign in"}
          </Button>

          <p className="text-sm text-[#786B63] mt-6">
            New here?{" "}
            <Link to="/register" className="text-[#C05A46] hover:underline" data-testid="link-register">
              Create an account
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
